import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { collabMaterialPreviewed, collabStandardsTabOpened, type AnalyticsEvent } from '../../core/events';
import type { PreviewKind } from '../../core/materials';
import type { HighlightAnchor } from '../../core/page-geometry';
import { isIsoStamp, readFrontier } from '../../core/unread';
import {
  call, COLLAB_UNREACHABLE, downloadMaterial, fetchTeacherDefault, markRead, renameMaterial,
  type CommentView, type GetToken, type GroupSummary, type MaterialSummary, type Saver,
} from '../api';
import type { PdfEngine } from '../pdf';
import type { PlannerSession } from '../planner-handoff';
import type { DrawPassage } from '../pdf-snapshot';
import { groupPath } from '../routes';
import CommentThread from './CommentThread';
import InlineRename from './InlineRename';
import { formatBytes } from '../../core/file-facts';
import { typeLabel } from './MaterialList';
import MaterialViewer from './MaterialViewer';
import StandardsCheck, { type StandardsSnapshot } from './StandardsCheck';

/**
 * One material, on its own page: the thing itself beside what is being said about it (U11).
 *
 * This used to be a panel the group room grew when a title was clicked. It is a route now,
 * because "open the thing I am looking at" is a place a teacher goes to — they expect the
 * back button to return them to the room, and they expect to be able to send a colleague in
 * the same group the address of the worksheet they are arguing about. Neither is possible
 * while the viewer is a piece of another screen's state.
 *
 * Two rules the file is built around, and both are the group room's rules rather than new
 * ones:
 *
 * 1. **The Worker decides, and this page asks it on every load.** Nothing about membership is
 *    derived here, and no id in the URL is trusted. A cold load fetches the group and its
 *    materials, and whatever the Worker answers is what renders.
 * 2. **A 404 is the same answer for a stranger, a removed member and a deleted material.**
 *    The Worker answers all three byte-identically on purpose (R5, AE1) and this page must
 *    not undo that by rendering three different sentences from one answer. What it *does*
 *    distinguish is *when* the refusal arrived: a 404 on the first load is "we could not find
 *    that", and a 404 on a page that was already open is "your access ended while you were
 *    looking at it" (AE4). That is a fact about the session, not about the id.
 *
 * ## The join between the document and the conversation
 *
 * The viewer knows where a passage is; the thread knows what was said about it. Neither can
 * own the link, so this page does — five small pieces of state and nothing else:
 *
 * - the **selection in hand**, which the viewer reports and the thread offers to comment on;
 * - the **anchors**, which the thread loads with its comments and the viewer draws;
 * - the **focused comment**, which either side can set, so clicking a highlight lights up its
 *   comment and clicking a comment lights up its highlight;
 * - a **scroll command**, nonce-keyed so it fires once rather than on every re-render;
 * - the **page count**, which is how the thread knows whether an anchor can still be placed.
 *
 * Lifting it here rather than wiring the two panels to each other is what keeps each of them
 * testable on its own, and it is why the thread needs no idea that a PDF exists.
 *
 * ## Marking the thread read
 *
 * Only what the teacher could have seen counts (R8, R9, KTD7). While the thread is loaded,
 * the sidebar is open and the Comments tab is the one selected, every rendered comment is
 * observed. What is sent is `readFrontier`: oldest first, the `createdAt` of the last comment
 * before the first colleague comment that has not entered the viewport — the teacher's own and
 * deleted comments never hold it back. Replies render under their parent, so the newest
 * comment on screen can be newer than one still below the fold; sending it would mark that
 * one read (R9). It is sent again only when the frontier moves forward. The Standards tab, a
 * folded sidebar or an unscrolled tail send nothing. A 404 is the same access-ended answer as
 * everywhere else; any other failure is silent, and the markers simply stay.
 */

export interface MaterialPageProps {
  groupId: string;
  materialId: string;
  getToken: GetToken;
  viewerUserId: string | null;
  track: (event: AnalyticsEvent) => void;
  /** Back to the group room. */
  onBack: () => void;
  /** Injected in tests so a download asserts without a real anchor click. */
  saver?: Saver;
  /** Injected in tests so the PDF viewer asserts without a real parser inside jsdom. */
  pdf?: PdfEngine;
  /** The Planner's hand-off held for this tab, handed to the Standards check (KTD5). */
  planner?: PlannerSession | null;
}

type Stage = 'loading' | 'ready' | 'missing' | 'failed' | 'lost';

/** The Worker's `MAX_TITLE_LENGTH`, the upload's limit too. */
const TITLE_MAX = 200;

export default function MaterialPage({
  groupId, materialId, getToken, viewerUserId, track, onBack, saver, pdf, planner = null,
}: MaterialPageProps) {
  const [stage, setStage] = useState<Stage>('loading');
  const [group, setGroup] = useState<GroupSummary | null>(null);
  const [material, setMaterial] = useState<MaterialSummary | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  /**
   * Whether the thread is on screen beside the document.
   *
   * Open by default, because a teacher arriving from a colleague's link is arriving *for* the
   * conversation. Closing it hands the whole width to the page, which is the only way a
   * letter-size worksheet is read at full size on a laptop.
   */
  const [commentsOpen, setCommentsOpen] = useState(true);
  // A Planner hand-off that named this material opens on its Standards check, ready to run
  // (planner collab materials R8, KTD6). Every other arrival opens on the comments.
  const [sideTab, setSideTab] = useState<'comments' | 'standards'>(
    planner?.handoff.target?.materialId === materialId ? 'standards' : 'comments',
  );

  /** The passage a teacher has selected but not yet said anything about. */
  const [pendingAnchor, setPendingAnchor] = useState<HighlightAnchor | null>(null);
  /** Bumped to tell the viewer the selection is spent — posted, or dropped. */
  const [selectionNonce, setSelectionNonce] = useState(0);
  /** What the thread is holding, which is where the viewer's anchors come from. */
  const [comments, setComments] = useState<CommentView[]>([]);
  const [focusedCommentId, setFocusedCommentId] = useState<string | null>(null);
  const [scrollTo, setScrollTo] = useState<{ commentId: string; nonce: number } | null>(null);
  const [pageCount, setPageCount] = useState<number | null>(null);
  const [drawPassage, setDrawPassage] = useState<DrawPassage | null>(null);

  const anchors = useMemo(
    () => comments
      .filter((comment) => !comment.deleted && comment.anchor !== null && comment.anchor !== undefined)
      .map((comment) => ({ commentId: comment.id, anchor: comment.anchor })),
    [comments],
  );

  const dropSelection = useCallback(() => {
    setPendingAnchor(null);
    setSelectionNonce((n) => n + 1);
  }, []);

  /** A comment was clicked: light it up, and move the document to its passage. */
  const focusComment = useCallback((commentId: string) => {
    setFocusedCommentId(commentId);
    setScrollTo((current) => ({ commentId, nonce: (current?.nonce ?? 0) + 1 }));
  }, []);

  /**
   * Whether this page ever rendered the material.
   *
   * It is the whole of the difference between "not found" and "access ended": before it is
   * true a 404 is the first answer we ever got, and after it is true a 404 is something that
   * changed underneath an open page.
   */
  const settled = useRef(false);

  const lost = useCallback(() => {
    setStage(settled.current ? 'lost' : 'missing');
  }, []);

  // Both in flight together: the room's name comes from one and the material from the
  // other, and a teacher waiting two round trips for one page is two round trips slower
  // than they need to be.
  const fetchRoom = useCallback(() => Promise.all([
    call<{ group: GroupSummary }>(getToken, `/api/groups/${encodeURIComponent(groupId)}`),
    call<{ materials: MaterialSummary[] }>(getToken, `/api/groups/${encodeURIComponent(groupId)}/materials`),
  ]), [getToken, groupId]);

  /**
   * The same two reads again, for the Standards check: the group default and this material's
   * override as the Worker holds them now. A 404 is access lost, as on every load; any other
   * failure, or a material no longer listed, is null and the check says it could not finish.
   */
  const reloadStandards = useCallback(async (): Promise<StandardsSnapshot | null> => {
    const [room, listing] = await fetchRoom();
    if (!room.ok || !listing.ok) {
      if ((!room.ok && room.status === 404) || (!listing.ok && listing.status === 404)) lost();
      return null;
    }
    const found = listing.data.materials.find((entry) => entry.id === materialId);
    if (!found) return null;
    return { standardsDefaults: room.data.group.standardsDefaults ?? null, standardsOverride: found.standardsOverride ?? null };
  }, [fetchRoom, materialId, lost]);

  /** The caller's own shared default, which the Standards check reads when it opens (KTD11). */
  const loadTeacherDefault = useCallback(() => fetchTeacherDefault(getToken), [getToken]);

  useEffect(() => {
    let live = true;
    setStage('loading');

    void (async () => {
      const [room, listing] = await fetchRoom();
      if (!live) return;

      if (!room.ok || !listing.ok) {
        const status = room.ok ? (listing.ok ? 0 : listing.status) : room.status;
        // 404 is the refusal. Anything else is a network or a server having a bad minute,
        // which is a retry rather than a statement about this teacher's access.
        if (status === 404) { lost(); return; }
        setStage('failed');
        return;
      }

      const found = listing.data.materials.find((entry) => entry.id === materialId) ?? null;
      if (found === null) {
        // The listing is the group's own answer about what is in it. A material that is not
        // in it — deleted, or never in this group — gets the same "could not find it" a
        // refused group gets, and names nothing.
        setStage('missing');
        return;
      }

      setGroup(room.data.group);
      setMaterial(found);
      settled.current = true;
      setStage('ready');
    })();

    return () => { live = false; };
  }, [fetchRoom, materialId, lost, attempt]);

  /** The ids of comments that have been on screen, and the newest stamp already sent. */
  const commentsPanel = useRef<HTMLDivElement>(null);
  const seen = useRef(new Set<string>());
  const sent = useRef<string | null>(null);
  const threadShown = stage === 'ready' && commentsOpen && sideTab === 'comments';

  const sawComments = useCallback((ids: string[]) => {
    for (const id of ids) seen.current.add(id);
    const frontier = readFrontier(comments, seen.current, viewerUserId);
    if (!isIsoStamp(frontier) || (sent.current !== null && frontier <= sent.current)) return;
    sent.current = frontier;
    void markRead(getToken, materialId, frontier).then((result) => {
      // Anything but a refusal leaves the markers as they were, and says nothing.
      if (!result.ok && result.status === 404) lost();
    });
  }, [comments, viewerUserId, getToken, materialId, lost]);

  useEffect(() => {
    const panel = commentsPanel.current;
    if (!threadShown || comments.length === 0 || panel === null) return undefined;
    const elements = Array.from(panel.querySelectorAll('[data-comment-id]'));
    const idOf = (element: Element) => element.getAttribute('data-comment-id') ?? '';
    // No IntersectionObserver (jsdom, an old browser): every rendered comment counts as seen,
    // as a thumbnail counts as near in `MaterialList`.
    if (typeof IntersectionObserver === 'undefined') {
      sawComments(elements.map(idOf));
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      sawComments(entries.filter((entry) => entry.isIntersecting).map((entry) => idOf(entry.target)));
    });
    for (const element of elements) observer.observe(element);
    return () => observer.disconnect();
  }, [threadShown, comments, sawComments]);

  async function download(target: MaterialSummary) {
    setDownloadError(null);
    const result = await downloadMaterial(getToken, target, saver);
    if (result.ok) return;
    if (result.status === 404) { lost(); return; }
    setDownloadError('We could not download that material. Try again in a moment.');
  }

  /**
   * A new title, for the uploader or an admin — the people the room offers Delete to. The
   * answer is the material's summary; what this page alone holds, like `unread`, is kept.
   */
  async function retitle(title: string): Promise<string | null> {
    const result = await renameMaterial(getToken, materialId, title);
    if (result.ok) {
      setMaterial((current) => (current ? { ...current, ...result.data.material } : result.data.material));
      return null;
    }
    if (result.status === 404) { lost(); return null; }
    if (result.status === 403) return 'Only the teacher who shared this, or an admin, can rename it.';
    if (result.status === 400) return `Use a title of up to ${TITLE_MAX} characters.`;
    return COLLAB_UNREACHABLE;
  }

  if (stage === 'lost') {
    // The same words the group room uses, because it is the same event: a membership that
    // ended while a page was open (AE4).
    return (
      <section className="panel" aria-label="Material">
        <h2>You no longer have access to this group.</h2>
        <p>
          Your membership ended while this page was open. Anything you shared stays with the
          group.
        </p>
        <button type="button" onClick={onBack}>Back to your groups</button>
      </section>
    );
  }

  if (stage === 'missing') {
    return (
      <section className="panel" aria-label="Material">
        <h2>We could not find that material.</h2>
        <p>
          It may have been removed from the group, or the link may be for a group you are not
          in.
        </p>
        <Link className="link" to={groupPath(groupId)}>Back to the group</Link>
      </section>
    );
  }

  if (stage === 'failed') {
    return (
      <section className="panel" aria-label="Material" role="alert">
        <h2>We could not open that material.</h2>
        <p>Try again in a moment.</p>
        <div className="row-actions">
          <button type="button" onClick={() => setAttempt((n) => n + 1)}>Try again</button>
          <Link className="link" to={groupPath(groupId)}>Back to the group</Link>
        </div>
      </section>
    );
  }

  if (stage === 'loading' || group === null || material === null) {
    return (
      <section className="panel" aria-label="Material">
        <p className="hint" role="status">Opening this material…</p>
      </section>
    );
  }

  return (
    // A plain wrapper, deliberately: the viewer below is the "Material" region and the thread
    // is the "Comments" one. A third landmark whose name contains theirs makes every
    // by-name query on this page ambiguous, which is how the browser suite first found it.
    <div className="material-page">
      {/* Where this page sits. One line, above the toolbar rather than inside it, so the
          toolbar is about the document and nothing else. */}
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link className="link" to={groupPath(groupId)}>{group.name}</Link>
      </nav>

      {/*
        * The toolbar, in the shape the study app's workshops PDF viewer uses: what you are
        * looking at on the left, what you can do with it on the right, immediately above the
        * document. It is this page's rather than the viewer's, because a teacher reading a
        * .docx that has no viewer still needs the download and the way out.
        */}
      <div className="toolbar material-toolbar">
        <div className="toolbar-title">
          <InlineRename
            canRename={group.role === 'admin' || material.uploadedBy === viewerUserId}
            value={material.title}
            noun="material"
            label="Title"
            maxLength={TITLE_MAX}
            onSave={retitle}
          >
            <h2>{material.title}</h2>
          </InlineRename>
          <p className="meta">
            {material.filename} · {typeLabel(material.contentType)} · {formatBytes(material.sizeBytes)}
          </p>
        </div>
        <div className="row-actions">
          {/*
            * The reading-mode switch. A document beside a conversation is the point of this
            * screen (U11), but a teacher who is actually *reading* wants the width — so the
            * thread folds away rather than the page growing a second layout.
            */}
          <button
            type="button"
            className="secondary"
            aria-pressed={!commentsOpen}
            onClick={() => setCommentsOpen((open) => !open)}
          >
            {commentsOpen ? (sideTab === 'comments' ? 'Hide comments' : 'Hide standards check') : (sideTab === 'comments' ? 'Show comments' : 'Show standards check')}
          </button>
          <button type="button" className="secondary" onClick={() => void download(material)}>
            Download
          </button>
          <Link className="button-link" to={groupPath(groupId)}>Back to the group</Link>
        </div>
      </div>

      {downloadError && <p className="error" role="alert">{downloadError}</p>}

      <div className={`workspace${commentsOpen ? '' : ' workspace-solo'}`}>
        <MaterialViewer
          key={`viewer-${material.id}`}
          material={material}
          getToken={getToken}
          {...(saver ? { urls: saver } : {})}
          {...(pdf ? { pdf } : {})}
          onDownload={(target) => { void download(target); }}
          onAccessLost={lost}
          onPreviewed={(kind: PreviewKind) => track(collabMaterialPreviewed(groupId, material.id, kind))}
          anchors={anchors}
          focusedCommentId={focusedCommentId}
          onSelect={(anchor) => {
            setPendingAnchor(anchor);
            // Selecting a passage is asking to say something about it. A thread folded away is
            // a comment box the teacher cannot reach, so the reading mode gives way.
            setCommentsOpen(true);
            setSideTab('comments');
          }}
          onAnchorActivated={(id) => { setFocusedCommentId(id); setCommentsOpen(true); setSideTab('comments'); }}
          onPageCount={setPageCount}
          // A function in state is set through an updater, or React calls it.
          onDrawPassage={(draw) => setDrawPassage(() => draw)}
          clearSelection={selectionNonce}
          scrollTo={scrollTo}
        />
        <aside className="material-sidebar" hidden={!commentsOpen}>
          <div role="tablist" aria-label="Material tools" className="material-tabs" onKeyDown={(event) => {
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
            event.preventDefault();
            const next = event.key === 'Home' ? 'comments' : event.key === 'End' ? 'standards' : sideTab === 'comments' ? 'standards' : 'comments';
            setSideTab(next);
            if (next === 'standards' && sideTab !== 'standards') track(collabStandardsTabOpened());
            document.getElementById(`${next}-tab`)?.focus();
          }}>
            <button id="comments-tab" role="tab" aria-selected={sideTab === 'comments'} aria-controls="comments-panel" tabIndex={sideTab === 'comments' ? 0 : -1} onClick={() => setSideTab('comments')}>Comments</button>
            <button id="standards-tab" role="tab" aria-selected={sideTab === 'standards'} aria-controls="standards-panel" tabIndex={sideTab === 'standards' ? 0 : -1} onClick={() => { if (sideTab !== 'standards') track(collabStandardsTabOpened()); setSideTab('standards'); }}>Standards check</button>
          </div>
          <div ref={commentsPanel} id="comments-panel" role="tabpanel" aria-labelledby="comments-tab" hidden={sideTab !== 'comments'}>
          <CommentThread
            key={material.id}
            materialId={material.id}
            getToken={getToken}
            viewerUserId={viewerUserId}
            isAdmin={group.role === 'admin'}
            onAccessLost={lost}
            pendingAnchor={pendingAnchor}
            onAnchorSettled={dropSelection}
            onComments={setComments}
            focusedCommentId={focusedCommentId}
            onFocusComment={focusComment}
            pageCount={pageCount}
            drawPassage={drawPassage}
          />
          </div>
          <div id="standards-panel" role="tabpanel" aria-labelledby="standards-tab" hidden={sideTab !== 'standards'}>
            <StandardsCheck
              key={material.id}
              material={material}
              getToken={getToken}
              onAccessLost={lost}
              standardsDefaults={group.standardsDefaults ?? null}
              standardsOverride={material.standardsOverride ?? null}
              reloadContext={reloadStandards}
              loadTeacherDefault={loadTeacherDefault}
              planner={planner}
            />
          </div>
        </aside>
      </div>
    </div>
  );
}
